import type { components } from '@connected/api-client/api.generated';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validators.generated';
import { usePatcher } from '@connected/api-client/hooks/use-patcher';
import { useElectricalProject } from '@connected/elektra-sdk/hooks/use-project';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import FieldInfo from '@connected/form-fields/components/field-info';
import { Label } from '@connected/form-fields/components/label';
import {
    SingleCheckOptionItem,
    SingleOptionField,
    SingleOptionItem,
} from '@connected/form-fields/components/option';
import { Accordion } from '@connected/ui/components/accordion';
import { Button } from '@connected/ui/components/button';
import { Input } from '@connected/ui/components/input';
import { Section } from '@connected/ui/features/layouts';
import { Container } from '@connected/ui/layouts/container';
import { useForm } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Option } from '@/hooks/use-option';

import { ElectricalInspectionKind } from '@/components/electrical-inspection-kind';
import { ElseField } from '@/components/else-field';
import { InspectionNormSelectionTable } from '@/components/inspection-norm-selection-table';
import { InspectionPlanReportFields } from '@/components/inspection-plan-report-fields';
import { FooterNavigation } from '@/components/navigation/footer-navigation';
import { useGenerateInspectionPlan } from '@/hooks/use-generate-inspection-plan';
import { queryClient } from '@/lib/query-client';
import { useOffline } from '@/stores/network-status';

export const Route = createFileRoute('/(auth)/projects/$projectVersionId/inspection-plans/information/')({
    component: Page,
    loader: ({ context, params }) => {
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.inspectionPlan.inspectionPlanEditOptions({ electricalProjectVersionId: params.projectVersionId }));
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.selectListsItems.selectListsItemsGetOptions({ electricalProjectVersionId: params.projectVersionId, listKind: 'AdditionalInstruction' }));
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.selectListsItems.selectListsItemsGetOptions({ electricalProjectVersionId: params.projectVersionId, listKind: 'InspectionComponent' }));
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.inspectionPlan.inspectionNormsListOptions({ electricalProjectVersionId: params.projectVersionId }));
    },
});

export type InspectionPlanReportFormValues = {
    inspectionPlanNumber?: string | null;
    inspectionPlanVersion?: string | null;
    sampleMethodKind?: components['schemas']['ElectricalInspectionSampleMethodKind'] | null;
    sampleMethodOther?: string | null;
    intervalDeterminationKind?: components['schemas']['ElectricalInspectionIntervalDeterminationKind'] | null;
    nextInspectionInYears?: number | null;
    intervalDeterminationOther?: string | null;
    additionalInstructions?: string[] | null;
    inspectionComponents?: string[] | null;
};

export type InspectionPlanFormValues = {
    projectNumber: string;
    shutdownMethodOther: string | null;
    shutdownMethodKind: components['schemas']['ElectricalPowerShutdownMethodKind'] | null;

    // non-combination mode
    inspectionPlanNumber: InspectionPlanReportFormValues['inspectionPlanNumber'];
    inspectionPlanVersion: InspectionPlanReportFormValues['inspectionPlanVersion'];
    sampleMethodKind: InspectionPlanReportFormValues['sampleMethodKind'];
    sampleMethodOther: InspectionPlanReportFormValues['sampleMethodOther'];
    intervalDeterminationKind: InspectionPlanReportFormValues['intervalDeterminationKind'];
    nextInspectionInYears: InspectionPlanReportFormValues['nextInspectionInYears'];
    intervalDeterminationOther: InspectionPlanReportFormValues['intervalDeterminationOther'];
    additionalInstructions: InspectionPlanReportFormValues['additionalInstructions'];
    inspectionComponents: InspectionPlanReportFormValues['inspectionComponents'];

    // combination mode
    reports: InspectionPlanReportFormValues[];
};

function Page() {
    const { handleGenerateInspectionPlan, disabled } = useGenerateInspectionPlan();

    return (
        <>
            <div className="flex flex-col gap-4">
                <Container>
                    <Container.Content>
                        <Form />
                    </Container.Content>
                    <Section.Footer>
                        <FooterNavigation>
                            <Button variant="default" onClick={handleGenerateInspectionPlan} disabled={disabled}>
                                Inspectieplan genereren
                            </Button>
                        </FooterNavigation>
                    </Section.Footer>
                </Container>
            </div>
        </>
    );
}

const shutdownMethodKindOptions: Option<components['schemas']['ElectricalPowerShutdownMethodKind']>[] = [
    {
        label: 'Conform schakelbrief opgesteld door installatieverantwoordelijke (zie bijlage indien van toepassing)',
        value: 'ConformDocumentSuppliedByInstallationAdministrator',
    },
    {
        label: 'Opdrachtgever zorgt voor uit - en inbedrijfname van de installatie',
        value: 'CustomerPerformsPowerShutdown',
    },
    {
        label: 'Inspectiebedrijf is gemachtigd om schakelhandelingen te verrichten in overleg met de gebruiker',
        value: 'InspectionCompanyIsAuthorized',
    },
    {
        label: 'Anders',
        value: 'Other',
    },
];

const defaultInspectionPlanFormValues: InspectionPlanFormValues = {
    projectNumber: '',
    shutdownMethodOther: null,
    shutdownMethodKind: null,
    inspectionPlanNumber: null,
    inspectionPlanVersion: null,
    sampleMethodKind: null,
    sampleMethodOther: null,
    intervalDeterminationKind: null,
    intervalDeterminationOther: null,
    nextInspectionInYears: null,
    additionalInstructions: [],
    inspectionComponents: [],
    reports: [],
};

const defaultInspectionPlanReportFormValues: InspectionPlanFormValues['reports'][number] = {
    inspectionPlanNumber: '',
    inspectionPlanVersion: '',
    sampleMethodKind: null,
    sampleMethodOther: '',
    intervalDeterminationKind: null,
    nextInspectionInYears: 0,
    intervalDeterminationOther: '',
    additionalInstructions: [],
    inspectionComponents: [],
};

export type InspectionPlanReportOption = {
    value: string;
    label: string;
    /**
     * This is only used if context.isCombinationInspection is false (aka non-combination mode).
     * @deprecated
     */
    selected?: boolean;
    inspectionKinds: components['schemas']['ElectricalInspectionKind'][];
};

function Form() {
    const { t } = useTranslation('inspection-plans');

    const isUserOffline = useOffline();

    const params = Route.useParams();
    const { context, inspectionKindsArray } = useElectricalProject();

    // query data
    const inspectionPlanQuery = useQuery(elektraAppQueryOptions.project.inspectionPlan.inspectionPlanEditOptions({ electricalProjectVersionId: params.projectVersionId }));
    const inspectionnormsQuery = useQuery(elektraAppQueryOptions.project.inspectionPlan.inspectionNormsListOptions({ electricalProjectVersionId: params.projectVersionId }));
    const additionalInstructionSelectlistItemQuery = useQuery(elektraAppQueryOptions.project.selectListsItems.selectListsItemsGetOptions({ electricalProjectVersionId: params.projectVersionId, listKind: 'AdditionalInstruction' }));
    const inspectionComponentSelectlistItemQuery = useQuery(elektraAppQueryOptions.project.selectListsItems.selectListsItemsGetOptions({ electricalProjectVersionId: params.projectVersionId, listKind: 'InspectionComponent' }));

    const { patchField } = usePatcher('/api/v2/electrical/projects/inspectionplans/patch', {
        onSettled: () => {
            queryClient.invalidateQueries(elektraAppQueryOptions.project.inspectionPlan.inspectionPlanEditOptions({ electricalProjectVersionId: params.projectVersionId }));
        },
        scope: {
            id: params.projectVersionId,
        },
    }, { body: { ElectricalProjectVersionId: params.projectVersionId } });

    const additionalInstructionSelectlistItemOptions = useMemo<InspectionPlanReportOption[]>(() => additionalInstructionSelectlistItemQuery?.data?.Items.map(item => ({
        value: item.ElectricalProjectSelectListItemId!,
        label: item.Label!,
        selected: item.Selected ?? false,
        inspectionKinds: item.InspectionKinds ?? [],
    })) || [], [additionalInstructionSelectlistItemQuery?.data?.Items]);
    const inspectionComponentSelectlistItemOptions = useMemo<InspectionPlanReportOption[]>(() => inspectionComponentSelectlistItemQuery?.data?.Items.map(item => ({
        value: item.ElectricalProjectSelectListItemId!,
        label: item.Label!,
        selected: item.Selected ?? false,
        inspectionKinds: item.InspectionKinds ?? [],
    })) || [], [inspectionComponentSelectlistItemQuery?.data?.Items]);

    const inspectionPlanForm = useForm<InspectionPlanFormValues, ZodValidator>({
        defaultValues: {
            projectNumber: inspectionPlanQuery?.data?.InspectionPlan?.ProjectNumber || defaultInspectionPlanFormValues.projectNumber,
            shutdownMethodKind: inspectionPlanQuery?.data?.InspectionPlan?.ShutdownMethodKind || defaultInspectionPlanFormValues.shutdownMethodKind,
            shutdownMethodOther: inspectionPlanQuery?.data?.InspectionPlan?.ShutdownMethodOther || defaultInspectionPlanFormValues.shutdownMethodOther,

            // non-combination mode
            inspectionPlanNumber: inspectionPlanQuery?.data?.InspectionPlan?.InspectionPlanNumber || defaultInspectionPlanFormValues.inspectionPlanNumber,
            inspectionPlanVersion: inspectionPlanQuery?.data?.InspectionPlan?.InspectionPlanVersion || defaultInspectionPlanFormValues.inspectionPlanVersion,
            sampleMethodKind: inspectionPlanQuery?.data?.InspectionPlan?.SampleMethodKind || defaultInspectionPlanFormValues.sampleMethodKind,
            sampleMethodOther: inspectionPlanQuery?.data?.InspectionPlan?.SampleMethodOther || defaultInspectionPlanFormValues.sampleMethodOther,
            intervalDeterminationKind: inspectionPlanQuery?.data?.InspectionPlan?.IntervalDeterminationKind || defaultInspectionPlanFormValues.intervalDeterminationKind,
            nextInspectionInYears: inspectionPlanQuery?.data?.InspectionPlan?.NextInspectionInYears || defaultInspectionPlanFormValues.nextInspectionInYears,
            intervalDeterminationOther: inspectionPlanQuery?.data?.InspectionPlan?.IntervalDeterminationOther || defaultInspectionPlanFormValues.intervalDeterminationOther,
            additionalInstructions: additionalInstructionSelectlistItemOptions.filter(item => item.selected).map(item => item.value) ?? defaultInspectionPlanFormValues.additionalInstructions,
            inspectionComponents: inspectionComponentSelectlistItemOptions.filter(item => item.selected).map(item => item.value) ?? defaultInspectionPlanFormValues.inspectionComponents,

            // combination mode
            reports: inspectionKindsArray.map((inspectionKind) => {
                const report = inspectionPlanQuery?.data?.InspectionPlan?.Reports?.find(report => report.InspectionKind === inspectionKind);

                const additionalInstructions = additionalInstructionSelectlistItemOptions.filter(item => item.inspectionKinds?.includes(inspectionKind)).map(item => item.value);
                const inspectionComponents = inspectionComponentSelectlistItemOptions.filter(item => item.inspectionKinds?.includes(inspectionKind)).map(item => item.value);

                return {
                    inspectionPlanNumber: report?.InspectionPlanNumber || defaultInspectionPlanReportFormValues.inspectionPlanNumber,
                    inspectionPlanVersion: report?.InspectionPlanVersion || defaultInspectionPlanReportFormValues.inspectionPlanVersion,
                    sampleMethodKind: report?.SampleMethodKind || defaultInspectionPlanReportFormValues.sampleMethodKind,
                    sampleMethodOther: report?.SampleMethodOther || defaultInspectionPlanReportFormValues.sampleMethodOther,
                    intervalDeterminationKind: report?.IntervalDeterminationKind || defaultInspectionPlanReportFormValues.intervalDeterminationKind,
                    nextInspectionInYears: report?.NextInspectionInYears || defaultInspectionPlanReportFormValues.nextInspectionInYears,
                    intervalDeterminationOther: report?.IntervalDeterminationOther || defaultInspectionPlanReportFormValues.intervalDeterminationOther,
                    additionalInstructions: additionalInstructions ?? defaultInspectionPlanReportFormValues.additionalInstructions,
                    inspectionComponents: inspectionComponents ?? defaultInspectionPlanReportFormValues.inspectionComponents,
                };
            }),
        },
        validatorAdapter: zodValidator(),
    });

    const selectNormMutation = api.useMutation('patch', '/api/v2/electrical/projects/inspectionnorms/patch', {
        onMutate: async (variables) => {
            const body = variables?.body;

            if (!body) {
                return;
            }

            const queryOptions = elektraAppQueryOptions.project.inspectionPlan.inspectionNormsListOptions({ electricalProjectVersionId: params.projectVersionId });
            const previousData = queryClient.getQueryData(queryOptions.queryKey) as components['schemas']['ListElectricalProjectInspectionNormQueryResult'];

            const newData: components['schemas']['ListElectricalProjectInspectionNormQueryResult'] = {
                InspectionNorms: previousData?.InspectionNorms?.map((norm) => {
                    if (norm.ElectricalProjectInspectionNormId === body.ElectricalProjectInspectionNormId) {
                        const updatedNorm = { ...norm, Status: body.Status };

                        // Auto-associate with inspection kind if provided
                        if (body.AddInspectionKind) {
                            const currentInspectionKinds = norm.InspectionKinds || [];
                            if (!currentInspectionKinds.includes(body.AddInspectionKind)) {
                                updatedNorm.InspectionKinds = [...currentInspectionKinds, body.AddInspectionKind];
                            }
                        }

                        return updatedNorm;
                    }
                    return norm;
                }),
                Count: previousData?.Count,
            };

            queryClient.setQueryData(queryOptions.queryKey, newData);

            return { previousData, queryOptions };
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(elektraAppQueryOptions.project.inspectionPlan.inspectionNormsListOptions({ electricalProjectVersionId: params.projectVersionId }));
        },
        onError: async (_error, _variables, context: any) => {
            queryClient.setQueryData(context?.queryOptions.queryKey, context?.previousData);
        },
        scope: {
            id: params.projectVersionId,
        },
    });

    const removeNormMutation = api.useMutation('patch', '/api/v2/electrical/projects/inspectionnorms/patch', {
        onMutate: async (variables) => {
            const body = variables?.body;

            if (!body) {
                return;
            }

            const queryOptions = elektraAppQueryOptions.project.inspectionPlan.inspectionNormsListOptions({ electricalProjectVersionId: params.projectVersionId });
            const previousData = queryClient.getQueryData(queryOptions.queryKey) as components['schemas']['ListElectricalProjectInspectionNormQueryResult'];

            const newData: components['schemas']['ListElectricalProjectInspectionNormQueryResult'] = {
                InspectionNorms: previousData?.InspectionNorms?.map(norm =>
                    norm.ElectricalProjectInspectionNormId === body.ElectricalProjectInspectionNormId
                        ? { ...norm, Status: body.Status }
                        : norm,
                ),
                Count: previousData?.Count,
            };

            queryClient.setQueryData(queryOptions.queryKey, newData);

            return { previousData, queryOptions };
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(elektraAppQueryOptions.project.inspectionPlan.inspectionNormsListOptions({ electricalProjectVersionId: params.projectVersionId }));
        },
        onError: async (_error, _variables, context: any) => {
            queryClient.setQueryData(context?.queryOptions.queryKey, context?.previousData);
        },
        scope: {
            id: params.projectVersionId,
        },
    });

    return (
        <form className="flex flex-col gap-4">
            <inspectionPlanForm.Field
                name="projectNumber"
                validators={{
                    onChange: validators.patchElectricalProjectInspectionPlanCommand.projectNumber,
                    onChangeAsync: patchField('ProjectNumber'),
                    onChangeAsyncDebounceMs: patchField.debounceTime,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label label={t('information.form.projectNumber.label')}>
                        <Input
                            name={name}
                            value={state.value ?? ''}
                            onChange={(e) => {
                                handleChange(e.target.value);
                            }}
                            placeholder={t('information.form.projectNumber.placeholder')}
                            disabled={isUserOffline}
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </inspectionPlanForm.Field>
            <div className="grid grid-cols-2 gap-4">
                <inspectionPlanForm.Field
                    name="inspectionPlanNumber"
                    validators={{
                        onChange: validators.editElectricalProjectInspectionPlanCommand.inspectionPlanNumber,
                        onChangeAsync: patchField('InspectionPlanNumber'),
                        onChangeAsyncDebounceMs: patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label label={t('information.form.inspectionPlanNumber.label')}>
                            <Input
                                name={name}
                                value={state.value ?? ''}
                                onChange={(e) => {
                                    handleChange(e.target.value);
                                }}
                                placeholder={t('information.form.inspectionPlanNumber.placeholder')}
                                disabled={isUserOffline}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </inspectionPlanForm.Field>
                <inspectionPlanForm.Field
                    name="inspectionPlanVersion"
                    validators={{
                        onChange: validators.editElectricalProjectInspectionPlanCommand.inspectionPlanVersion,
                        onChangeAsync: patchField('InspectionPlanVersion'),
                        onChangeAsyncDebounceMs: patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label label={t('information.form.inspectionPlanVersion.label')}>
                            <Input
                                name={name}
                                value={state.value ?? ''}
                                onChange={(e) => {
                                    handleChange(e.target.value);
                                }}
                                placeholder={t('information.form.inspectionPlanVersion.placeholder')}
                                disabled={isUserOffline}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </inspectionPlanForm.Field>
            </div>

            {/* Shutdown method for all inspection types */}
            <inspectionPlanForm.Field
                name="shutdownMethodKind"
                validators={{
                    onChange: validators.editElectricalProjectInspectionPlanCommand.shutdownMethodKind,
                    onChangeAsync: async (value) => {
                        const response = await patchField('ShutdownMethodKind')(value);

                        if (value?.value !== 'Other') {
                            inspectionPlanForm.setFieldValue('shutdownMethodOther', '');

                            await queryClient.invalidateQueries(elektraAppQueryOptions.project.inspectionPlan.inspectionPlanEditOptions({ electricalProjectVersionId: params.projectVersionId }));
                        }

                        return response;
                    },
                    onChangeAsyncDebounceMs: patchField.debounceTime,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label label={t('information.form.method-of-commissioning.label')}>
                        <SingleOptionField
                            name={name}
                            handleChange={handleChange}
                            state={state}
                            options={shutdownMethodKindOptions}
                            render={SingleOptionItem}
                            dropdown
                            disabled={isUserOffline}
                            closeOnClick
                            placeholder="Selecteer wijze van uit- en inbedrijfname"
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </inspectionPlanForm.Field>

            <inspectionPlanForm.Subscribe selector={state => [state.values.shutdownMethodKind]}>
                {([shutdownMethodKind]) =>
                    shutdownMethodKind === 'Other' && (
                        <inspectionPlanForm.Field
                            name="shutdownMethodOther"
                            validators={{
                                onChange: validators.editElectricalProjectInspectionPlanCommand.shutdownMethodOther,
                                onChangeAsync: patchField('ShutdownMethodOther'),
                                onChangeAsyncDebounceMs: patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <ElseField>
                                    <Label label="Anders" className="w-full">
                                        <Input
                                            name={name}
                                            value={state.value ?? ''}
                                            onChange={e => handleChange(e.target.value)}
                                            placeholder="Anders"
                                            disabled={isUserOffline}
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                </ElseField>
                            )}
                        </inspectionPlanForm.Field>
                    )}
            </inspectionPlanForm.Subscribe>

            {/* Norms section for all inspection types */}
            <div className="flex flex-col gap-4">
                <Label
                    label={t('information.form.norms.label')}
                    className="w-full"
                >
                    <SingleOptionField
                        handleChange={async (normId) => {
                            const hasSingleInspectionKind = context.selectedInspectionKinds.size === 1;
                            const shouldAutoAssociate = !context.isCombinationInspection || hasSingleInspectionKind;
                            const body: {
                                ElectricalProjectInspectionNormId: string;
                                Status: 'Selected';
                                AddInspectionKind?: components['schemas']['ElectricalInspectionKind'];
                            } = {
                                ElectricalProjectInspectionNormId: normId,
                                Status: 'Selected',
                            };

                            if (shouldAutoAssociate && hasSingleInspectionKind) {
                                body.AddInspectionKind = inspectionKindsArray[0];
                            }

                            selectNormMutation.mutate({
                                body,
                            });
                        }}
                        state={{ value: '' }}
                        options={inspectionnormsQuery?.data?.InspectionNorms?.filter(x => x.Status !== 'Selected').map(item => ({
                            label: item.Name!,
                            value: item.ElectricalProjectInspectionNormId!,
                        })) || []}
                        render={SingleCheckOptionItem}
                        dropdown
                        disabled={isUserOffline}
                        emptyOptionsMessage="Geen normen beschikbaar"
                        placeholder="Kies normen"
                        closeOnClick
                    />
                </Label>

                <InspectionNormSelectionTable
                    norms={inspectionnormsQuery?.data?.InspectionNorms || []}
                    inspectionKinds={inspectionKindsArray}
                    projectVersionId={params.projectVersionId}
                    isCombinationInspection={context.isCombinationInspection ?? false}
                    onRemove={(normId) => {
                        removeNormMutation.mutate({
                            body: {
                                ElectricalProjectInspectionNormId: normId,
                                Status: 'NotSelected',
                            },
                        });
                    }}
                />
            </div>

            {/* Multi-inspection UI (Combination Inspection) */}
            {context.isCombinationInspection && context.selectedInspectionKinds.size > 1 && (
                <div className="flex flex-col gap-4">
                    <h2 className="text-lg font-semibold">Inspectieplan per inspectie</h2>
                    <Accordion type="single" defaultValue={inspectionKindsArray[0]} collapsible>
                        {inspectionKindsArray.map((inspectionKind, index) => (
                            <Accordion.Item key={inspectionKind} value={inspectionKind}>
                                <Accordion.Trigger>
                                    <h3 className="text-lg font-semibold">
                                        <ElectricalInspectionKind inspectionKind={inspectionKind} />
                                    </h3>
                                </Accordion.Trigger>
                                <Accordion.Content className="flex flex-col gap-6">
                                    <InspectionPlanReportFields
                                        form={inspectionPlanForm}
                                        reportIndex={index}
                                        inspectionKind={inspectionKind}
                                        projectVersionId={params.projectVersionId}
                                        additionalInstructionSelectlistItemOptions={additionalInstructionSelectlistItemOptions}
                                        inspectionComponentSelectlistItemOptions={inspectionComponentSelectlistItemOptions}
                                    />
                                </Accordion.Content>
                            </Accordion.Item>
                        ))}
                    </Accordion>
                </div>
            )}

            {/* Single inspection UI (Non-combination inspection) */}
            {(!context.isCombinationInspection || context.selectedInspectionKinds.size === 0) && context.selectedInspectionKinds.size > 0 && (
                <InspectionPlanReportFields
                    form={inspectionPlanForm}
                    reportIndex={0}
                    inspectionKind={inspectionKindsArray[0]}
                    projectVersionId={params.projectVersionId}
                    isSingleClassification={true}
                    additionalInstructionSelectlistItemOptions={additionalInstructionSelectlistItemOptions}
                    inspectionComponentSelectlistItemOptions={inspectionComponentSelectlistItemOptions}
                />
            )}
        </form>
    );
}
